import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../helpers/api";
import { motion, AnimatePresence } from "framer-motion";
import { formatCreatedDate, formatDateTimeForAPI, urgencyColors, reactiveStatusOptions, getWorkTypeBadgeColor, reactiveEnumTranslations } from "../constants/utils";
import { normalizeWorkOrderView, workOrderViewOptions } from "../constants/workOrderViews";
import { Card, CardBody, CardHeader, Modal, Row, Col, Input, Button, Spinner, Badge, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import FeatherIcon from "feather-icons-react";
import * as XLSX from "xlsx";
import FileModal from "../components/Common/importExportModal";
import usePermission from "../Hooks/usePermission";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import LocationModal from "../components/LocationModal";
import TicketDetailsModal from "../components/TicketDetailsModal";
import ReportedByModal from "../components/ReportedByModal";
import { MdSort } from "react-icons/md";
import TechnicianModal from "../components/TechnicianModal";
import AssetModal from "../components/AssetModal";
import AssignTechnicianModal from "../components/AssignTechnicianModal";
import PermissionGuard from "../components/Common/PermissionGuard";
import WorkOrderForm from "../components/WorkOrderForm";
import { WorkOrderStatusBadge } from "../components/Common";
import WorkOrderCalendarGrid from "../components/WorkOrderCalendarGrid";
import WorkOrderShortcuts, { WORK_ORDER_SHORTCUTS } from "../components/WorkOrderShortcuts";
import Select from "react-select";
import { useTranslation } from 'react-i18next';

/**
 * Turns an API failure into text React can actually render.
 *
 * The backend answers a rejected request with {status, message, response:{}, error:{}} — `error` is
 * an EMPTY OBJECT, not a string. Reading it directly handed React an object, which throws
 * "Objects are not valid as a React child" and blanks the page; and because `{}` is truthy, the
 * human-readable `message` (already localized server-side) was never reached.
 *
 * @param {object} error the axios error
 * @param {Function} t i18n translate, for the last-resort fallback
 * @returns {string} a renderable message
 */
const apiErrorMessage = (error, t) => {
  const data = error?.response?.data;
  const candidate = [data?.message, data?.error, data?.warning].find(
    (value) => typeof value === 'string' && value.trim() !== ''
  );
  return candidate || t("shared.something_went_wrong", "Something went wrong!");
};

const buildShortcutParams = (shortcutKey, currentUserId) => {
  if (!shortcutKey) return null;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfTomorrow = new Date(startOfToday);
  startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);
  const sevenDaysAgo = new Date(startOfToday);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const iso = (d) => d.toISOString().split(".")[0];
  switch (shortcutKey) {
    case "due_today":
      return { dueDateFrom: iso(startOfToday), dueDateTo: iso(startOfTomorrow), notStatus: "RESOLVED" };
    case "overdue":
      return { dueDateTo: iso(now), notStatus: "RESOLVED" };
    case "high_priority":
      return { urgencyLevel: "high", notStatus: "RESOLVED" };
    case "in_progress":
      return { status: "IN_PROGRESS" };
    case "assigned_to_me":
      return currentUserId ? { assignedTechnician: currentUserId, notStatus: "RESOLVED" } : { notStatus: "RESOLVED" };
    case "completed_last_7_days":
      return { status: "RESOLVED", resolvedAtFrom: iso(sevenDaysAgo) };
    default:
      return null;
  }
};

const SHORTCUT_TARGET_TAB = {
  completed_last_7_days: "resolved",
};

const Transactions = () => {
  const { t } = useTranslation();
  document.title = `${t("shared.work_orders", "Work Orders")} | Arcmedix`;
  const [searchParams, setSearchParams] = useSearchParams();
  const [technicians, setTechnicians] = useState(null);
  const [locations, setLocations] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [filterAssets, setFilterAssets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [filterModal, setFilterModal] = useState(false);
  const [modal_large, setmodal_large] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [originalEntry, setOriginalEntry] = useState(null); // Track original values for clearFields
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [deleteModal, setDeleteModal] = useState(false);
  const [sortModal, setSortModal] = useState(false);
  const [assetModal, setAssetModal] = useState({
    isOpen: false,
    userData: null,
    loading: false
  });
  const [sortCriteria, setSortCriteria] = useState({
    field: 'createdAt',
    direction: 'desc'
  });
  const user = JSON.parse(localStorage.getItem('authUser'));
  const organizationId = user?.activeOrganizationId ?? null;
  const currentUserId = user?.id;
  const [activeTab, setActiveTab] = useState('todo'); // 'todo' or 'resolved'
  const [activeShortcut, setActiveShortcut] = useState(() => searchParams.get('shortcut') || null);

  const [dateRangeModal, setDateRangeModal] = useState(false);
  const [locationModal, setLocationModal] = useState({
    isOpen: false,
    title: '',
    mainData: null,
    relatedData: [],
    loading: false,
    locationData: null,
  });
  const [ticketModal, setTicketModal] = useState({
    isOpen: false,
    ticketData: null,
    loading: false
  });
  const [reportedByModal, setReportedByModal] = useState({
    isOpen: false,
    userData: null,
    loading: false
  });
  const [count, setCount] = useState(10);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const { visibilityRestricted } = usePermission();
  const [searchTerm, setSearchTerm] = useState("");
  const [enumData, setEnumData] = useState([]);
  const [fileModal, setFileModal] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [technicianModal, setTechnicianModal] = useState({
    isOpen: false,
    technicianData: null,
    loading: false
  });
  const [assignTechnicianModal, setAssignTechnicianModal] = useState({
    isOpen: false,
    ticketId: null,
    pendingTransaction: null
  });
  // Work-type view (REACTIVE | PREVENTIVE | ALL): URL param → organization default → REACTIVE.
  const [filters, setFilters] = useState(() => ({
    status: "",
    urgencyLevel: "",
    workType: normalizeWorkOrderView(searchParams.get('workType'))
      ?? normalizeWorkOrderView(user?.activeOrganizationDefaultWorkOrderView)
      ?? 'REACTIVE',
    categoryId: "",
    assignedTeamId: "",
    impactOnCare: "",
    usageStatus: "",
    assetIds: [],
    locationIds: [],
  }));
  const [orgTeamsForFilter, setOrgTeamsForFilter] = useState([]);

  // Edit scope dialog for preventive recurring work orders
  const [editScopeDialog, setEditScopeDialog] = useState({ isOpen: false, pendingPayload: null });

  // Calendar view state
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'calendar'
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());

  const [dropdownOpen, setDropdownOpen] = useState({});

  const toggleDropdown = (ticketId) => {
    setDropdownOpen(prev => ({
      ...prev,
      [ticketId]: !prev[ticketId]
    }));
  };

  // Helper function to convert decimal hours to HH:MM:SS.mmm format
  const convertHoursToTimeFormat = (hours) => {
    if (!hours || isNaN(hours)) return '';

    const totalHours = Math.floor(hours);
    const minutes = Math.floor((hours - totalHours) * 60);
    const seconds = Math.floor(((hours - totalHours) * 60 - minutes) * 60);
    const milliseconds = Math.floor((((hours - totalHours) * 60 - minutes) * 60 - seconds) * 1000);

    return `${totalHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
  };

  // Helper function to convert HH:MM:SS.mmm format back to decimal hours for display
  const convertTimeFormatToHours = (timeString) => {
    if (!timeString) return '';

    const timeParts = timeString.split(':');
    if (timeParts.length !== 3) return '';

    const hours = parseInt(timeParts[0]) || 0;
    const minutes = parseInt(timeParts[1]) || 0;
    const secondsAndMs = timeParts[2].split('.');
    const seconds = parseInt(secondsAndMs[0]) || 0;
    const milliseconds = parseInt(secondsAndMs[1]) || 0;

    return hours + (minutes / 60) + (seconds / 3600) + (milliseconds / 3600000);
  };

  // Build clearFields array for fields that were explicitly cleared (had value -> now empty)
  const buildClearFields = (current, original) => {
    if (!original) return [];

    const clearableFields = [
      { key: 'locationId', enum: 'LOCATION_ID' },
      { key: 'preferredTechnician', enum: 'PREFERRED_TECHNICIAN' },
      { key: 'resolvedBy', enum: 'RESOLVED_BY' },
      { key: 'resolvedAt', enum: 'RESOLVED_AT' },
      { key: 'usageStatus', enum: 'USAGE_STATUS' },
      { key: 'urgencyLevel', enum: 'URGENCY_LEVEL' },
      { key: 'estimatedTime', enum: 'ESTIMATED_TIME' },
      { key: 'errorCode', enum: 'ERROR_CODE' },
      { key: 'resolutionNotes', enum: 'RESOLUTION_NOTES' },
    ];

    return clearableFields
      .filter(({ key }) => {
        const originalValue = original[key];
        let currentValue = current[key];

        // Handle special cases for object values (preferredTechnician, resolvedBy)
        if (currentValue && typeof currentValue === 'object' && currentValue.id !== undefined) {
          currentValue = currentValue.id;
        }

        const wasSet = originalValue != null && originalValue !== '';
        const isNowEmpty = currentValue == null || currentValue === '';
        return wasSet && isNowEmpty;
      })
      .map(({ enum: e }) => e);
  };

  const toggleTab = (tab) => {
    const wasCalendar = viewMode !== 'list';
    if (activeTab !== tab || wasCalendar) {
      setActiveTab(tab);
      setViewMode('list');
      setTransactions([]);
      setCount(10);
      setHasMore(true);
      // If returning from calendar to the same tab, the useEffect won't fire
      // because activeTab didn't change. Force a refetch after state settles.
      if (wasCalendar && activeTab === tab) {
        setTimeout(() => fetchAssetIssueFromOrganization(), 0);
      }
    }
  };

  const handleShortcutClick = (shortcutKey) => {
    setActiveShortcut(shortcutKey);
    setTransactions([]);
    setCount(10);
    setHasMore(true);
    // Only switch tabs/view when the user is on the list view.
    // On calendar, stay on calendar and let the shortcut filter the events in place.
    if (viewMode === 'list') {
      const targetTab = shortcutKey ? (SHORTCUT_TARGET_TAB[shortcutKey] || 'todo') : activeTab;
      if (targetTab !== activeTab) setActiveTab(targetTab);
    }
    const next = new URLSearchParams(searchParams);
    if (shortcutKey) next.set('shortcut', shortcutKey);
    else next.delete('shortcut');
    setSearchParams(next, { replace: true });
  };

  const handleWorkTypeViewChange = (view) => {
    const next = normalizeWorkOrderView(view);
    if (!next || next === filters.workType) return;
    // The `filters` effect clears the rows, resets pagination and refetches.
    setFilters((prev) => ({ ...prev, workType: next }));
    // Mirror to the URL so the view survives opening a ticket and navigating back.
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('workType', next);
    setSearchParams(nextParams, { replace: true });
  };

  const handlePriorityChange = async (transaction, newPriority) => {
    try {
      await api.put("asset-issues/update", {
        id: transaction.id,
        urgencyLevel: newPriority,
      });
      fetchAssetIssueFromOrganization();
    } catch (error) {
      console.error("Failed to update priority:", error);
    }
  };


const handleCreate = async () => {
  setApiError('');
    const formErrors = validateForm();
  if (Object.keys(formErrors).length > 0) {
    setErrors(formErrors);
    return false;
  }

  setLoading(true);
  try {
    const payload = {
      assetId: selectedEntry.assetId,
      title: selectedEntry.title,
      workType: selectedEntry.workType || 'OTHER',
      usageStatus: selectedEntry.usageStatus,
      downtimeReasonId: selectedEntry.usageStatus === 'OFFLINE' ? (selectedEntry.downtimeReasonId || null) : null,
      urgencyLevel: selectedEntry.urgencyLevel,
      description: selectedEntry.description,
      status: 'SUBMITTED',
      firstOccurrence: selectedEntry.firstOccurrence || formatDateTimeForAPI(new Date()),
      requestDate: selectedEntry.requestDate || formatDateTimeForAPI(new Date()),
      estimatedTime: selectedEntry.estimatedTime ? convertHoursToTimeFormat(parseFloat(selectedEntry.estimatedTime)) : '',
      preferredTechnician: selectedEntry.preferredTechnician?.id || selectedEntry.preferredTechnician,
      assignedTechnician: Array.isArray(selectedEntry.assignedTechnician)
        ? selectedEntry.assignedTechnician.map(tech => tech.id || tech)
        : selectedEntry.assignedTechnician?.id
          ? [selectedEntry.assignedTechnician.id]
          : selectedEntry.assignedTechnician
            ? [selectedEntry.assignedTechnician]
            : [],
      assignedTeamIds: Array.isArray(selectedEntry.assignedTeams)
        ? selectedEntry.assignedTeams.map(tm => tm.id || tm)
        : [],
      resolutionNotes: selectedEntry.resolutionNotes || '',
      imageUrls: selectedEntry.imageUrls || [],
      documentUrls: selectedEntry.documentUrls || [],
      categoryIds: selectedEntry.categoryIds || [],
      vendorIds: selectedEntry.vendorIds || [],
      locationId: selectedEntry.locationId || null,
      // Recurrence fields
      recurrenceType: selectedEntry.recurrenceType || 'NONE',
      recurrenceDays: selectedEntry.recurrenceDays || [],
      recurrenceInterval: selectedEntry.recurrenceInterval || 1,
      recurrenceDayOfMonth: selectedEntry.recurrenceDayOfMonth || null,
      occurrences: selectedEntry.occurrences || null,
      recurrenceEndDate: selectedEntry.recurrenceEndDate || null
    };

    const { data } = await api.post(`asset-issues/create`, payload);
    fetchAssetIssueFromOrganization();
    handleModalClose();
  } catch (error) {
    setApiError(apiErrorMessage(error, t));
  } finally {
    setLoading(false);
  }
}

  const handleTechnicianClick = async (technicianId) => {
    if (!technicianId) return;

    setTechnicianModal(prev => ({
      ...prev,
      isOpen: true,
      technicianData: null,
      loading: true
    }));

    try {
      const { data: technicianData } = await api.get(`user/get-all?id=${technicianId}`);
      const { data: ticketsData } = await api.get(`asset-issues/organization/filtered`, {
        params: { 
          assignedTechnician: technicianId,
          count: 5
        }
      });

      setTechnicianModal(prev => ({
        ...prev,
        technicianData: {
          ...technicianData.response[0],
          assignedTickets: ticketsData.response || []
        },
        loading: false
      }));
    } catch (error) {
      console.error('Error fetching technician details:', error);
      setTechnicianModal(prev => ({ ...prev, loading: false }));
    }
  };

  const fetchTechnicians = async () => {
    try {
      setLoading(true);
      const response = await api.get(`user/technicians?organizationId=${organizationId}`);
      setTechnicians(response.data.response || []);
    } catch (error) {
      console.error('Error fetching technicians:', error);
    } finally {
      setLoading(false);
    }
  };

  // Slice 6 — load teams for the WO list filter dropdown.
  const fetchTeamsForFilter = useCallback(async () => {
    try {
      const { data } = await api.get('team/all');
      setOrgTeamsForFilter(Array.isArray(data?.response) ? data.response : []);
    } catch (error) {
      console.error('Error fetching teams for filter:', error);
    }
  }, []);

  const handleDelete = async () => {
    try {
      await api.delete(`asset-issues/delete?id=${selectedEntry?.id}`);
      fetchAssetIssueFromOrganization();
      setDeleteModal(false);
    } catch (error) {
      console.error("Error deleting user:", error);
      setApiError(t("shared.failed_delete", "Failed to delete user"));
    }
  };

  const onStatusChange = async (data, value) => {
    if (value === 'PENDING') {
      // Open assign technician modal for pending status
      setAssignTechnicianModal({
        isOpen: true,
        ticketId: data?.id,
        pendingTransaction: data
      });
      return;
    }

    try {
      const payload = { id: data?.id, status: value };
      // Set resolvedAt timestamp when marking as RESOLVED
      if (value === 'RESOLVED') {
        payload.resolvedAt = new Date().toISOString();
      }
      const response = await api.put('asset-issues/update', payload);
      fetchAssetIssueFromOrganization()
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleAssignTechnician = async (ticketId, technicianIds, teamIds = []) => {
    try {
      // First update status to PENDING
      const statusPayload = { id: ticketId, status: 'PENDING' };
      await api.put('asset-issues/update', statusPayload);

      // Then assign technicians + teams in a single update so the backend can
      // diff against existing assignments and fan out notifications.
      const assignPayload = {
        id: ticketId,
        assignedTechnician: Array.isArray(technicianIds) ? technicianIds : [technicianIds],
        assignedTeamIds: Array.isArray(teamIds) ? teamIds : [teamIds],
      };
      await api.put('asset-issues/update', assignPayload);

      // Close modal and refresh data
      setAssignTechnicianModal({
        isOpen: false,
        ticketId: null,
        pendingTransaction: null
      });

      fetchAssetIssueFromOrganization();
    } catch (error) {
      console.error('Error assigning technicians/teams:', error);
    }
  };


  const getEnumValues = (enumName) => {
    const enumItem = enumData.find(item => item.name === enumName);
    return enumItem ? enumItem.values : [];
  };

  const handleDetailsClick = async (type, data, contextData = null) => {
    // Reactive only opens location detail today; asset/org rows never reach this handler.
    if (type !== 'location') return;
    setLocationModal({
      isOpen: true,
      title: t("shared.location_details", 'Location Details'),
      mainData: String(data),
      relatedData: [],
      loading: true,
      locationData: { locationName: String(data), organization: contextData },
    });
    try {
      const { data: response } = await api.get('asset-issues/organization/filtered', {
        params: { organizationLocation: String(data), organizationId },
      });
      setLocationModal(prev => ({ ...prev, relatedData: response.response || [], loading: false }));
    } catch (error) {
      console.error('Error fetching location work orders:', error);
      setLocationModal(prev => ({ ...prev, loading: false }));
    }
  };

  const tog_large = (entry = null) => {
    setmodal_large(!modal_large);
    if (entry) {
      // Convert estimated time from HH:MM:SS.mmm format to decimal hours for editing
      const hoursValue = entry.estimatedTime ? convertTimeFormatToHours(entry.estimatedTime) : '';

      // Get locationId from asset's location if available
      const locationId = entry.locationId ||
        (entry.asset?.currentLocation ? locations?.find(loc => loc.locationName === entry.asset.currentLocation)?.id : null);

      const entryData = {
        ...entry,
        assetId: entry.assetId || entry.asset?.id || '',
        estimatedTime: hoursValue ? hoursValue.toString() : '',
        locationId: locationId || '',
        usageStatus: entry.asset?.assetStatus || entry.usageStatus || '',
        imageUrls: entry.imageUrls || []
      };

      setSelectedEntry(entryData);
      // Store original values for clearFields comparison
      setOriginalEntry({
        locationId: locationId || null,
        preferredTechnician: entry.preferredTechnician?.id || entry.preferredTechnician || null,
        resolvedBy: entry.resolvedBy?.id || entry.resolvedBy || null,
        resolvedAt: entry.resolvedAt || null,
        usageStatus: entry.asset?.assetStatus || entry.usageStatus || null,
        urgencyLevel: entry.urgencyLevel || null,
        estimatedTime: entry.estimatedTime || null,
        errorCode: entry.errorCode || null,
        resolutionNotes: entry.resolutionNotes || null,
      });

    } else {
      setSelectedEntry(null);
      setOriginalEntry(null);
    }
    setErrors({});
    setApiError('');
  };

  const handleReportedByClick = async (userId) => {
    if (!userId) return;

    setReportedByModal(prev => ({
      ...prev,
      isOpen: true,
      userData: null,
      loading: true
    }));

    try {
      const { data: userData } = await api.get(`user/get-all?id=${userId}`);
      const { data: issuesData } = await api.get(`asset-issues/organization/filtered`, {
        params: { 
          organizationId,
          reportedBy: userId,
          count: 5
        }
      });

      setReportedByModal(prev => ({
        ...prev,
        userData: {
          ...userData.response[0],
          reportedIssues: issuesData.response || []
        },
        loading: false
      }));
    } catch (error) {
      console.error('Error fetching reported by details:', error);
      setReportedByModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleAssetClick = async (userId, cachedAsset = null, fromDate = null, toDate = null) => {
          if (!userId) return;

          setAssetModal(prev => ({
            ...prev,
            isOpen: true,
            userData: null,
            loading: true
          }));

          try {
            const { data: userData } = await api.get(`asset/get-by-id?id=${userId}`);

            const params = {
              // assetId: userId, // Commented out: asset filtering disabled for Reactive Work Orders
              organizationId,
              count: 5
            };

            if (fromDate) params.fromDate = fromDate;
            if (toDate) params.toDate = toDate;

            const { data: issuesData } = await api.get(`asset-issues/organization/filtered`, { params });

            setAssetModal(prev => ({
              ...prev,
              userData: {
                ...userData.response,
                reportedIssues: issuesData.response || []
              },
              loading: false
            }));
          } catch (error) {
            console.error('Error fetching reported by details:', error);
            if (cachedAsset) {
              setAssetModal({ isOpen: true, userData: { ...cachedAsset, record: false }, loading: false });
            } else {
              setAssetModal(prev => ({ ...prev, loading: false }));
            }
          }
        };

  const validateForm = () => {
    const newErrors = {};

    // Only title is required
    if (!selectedEntry?.title?.trim()) newErrors.title = t("shared.title_required", "Title is required");

    if (selectedEntry?.status === "PENDING" && (!selectedEntry?.assignedTechnician || selectedEntry.assignedTechnician.length === 0)) {
      newErrors.assignedTechnician = t("business.reactive.technician_required_pending", "At least one technician is required for pending work orders");
    }

    // Require recurrence for PREVENTIVE work type
    if (selectedEntry?.workType === 'PREVENTIVE' && (!selectedEntry?.recurrenceType || selectedEntry.recurrenceType === 'NONE')) {
      newErrors.recurrenceType = t("business.reactive.recurrence_required", "Recurrence is required for preventive work orders");
    }

    // Require at least one day for WEEKLY recurrence (Daily means every day, no selection needed)
    if (selectedEntry?.recurrenceType === 'WEEKLY' &&
        (!selectedEntry?.recurrenceDays || selectedEntry.recurrenceDays.length === 0)) {
      newErrors.recurrenceDays = t("business.reactive.one_day_required", "At least one day must be selected");
    }

    return newErrors;
};

  // Build the update payload (shared between direct update and scope dialog)
  const buildUpdatePayload = (/* applyToFollowing = null — disabled */) => {
    const clearFields = buildClearFields(selectedEntry, originalEntry);
    return {
      id: selectedEntry.id,
      title: selectedEntry.title,
      workType: selectedEntry.workType || 'OTHER',
      usageStatus: selectedEntry.usageStatus || null,
      downtimeReasonId: selectedEntry.usageStatus === 'OFFLINE' ? (selectedEntry.downtimeReasonId || null) : null,
      urgencyLevel: selectedEntry.urgencyLevel || null,
      description: selectedEntry.description,
      status: selectedEntry.status,
      firstOccurrence: selectedEntry.firstOccurrence || formatDateTimeForAPI(new Date()),
      preferredTechnician: selectedEntry.preferredTechnician?.id || selectedEntry.preferredTechnician || null,
      requestDate: selectedEntry?.requestDate,
      estimatedTime: selectedEntry.estimatedTime ? convertHoursToTimeFormat(parseFloat(selectedEntry.estimatedTime)) : null,
      assignedTechnician: Array.isArray(selectedEntry.assignedTechnician)
        ? selectedEntry.assignedTechnician.map(tech => tech.id || tech)
        : selectedEntry.assignedTechnician?.id
          ? [selectedEntry.assignedTechnician.id]
          : selectedEntry.assignedTechnician
            ? [selectedEntry.assignedTechnician]
            : [],
      assignedTeamIds: Array.isArray(selectedEntry.assignedTeams)
        ? selectedEntry.assignedTeams.map(tm => tm.id || tm)
        : [],
      imageUrls: selectedEntry.imageUrls || [],
      documentUrls: selectedEntry.documentUrls || [],
      categoryIds: selectedEntry.categoryIds || [],
      vendorIds: selectedEntry.vendorIds || [],
      locationId: selectedEntry.locationId || null,
      recurrenceType: selectedEntry.recurrenceType || 'NONE',
      recurrenceDays: selectedEntry.recurrenceDays || [],
      recurrenceInterval: selectedEntry.recurrenceInterval || 1,
      recurrenceDayOfMonth: selectedEntry.recurrenceDayOfMonth || null,
      occurrences: selectedEntry.occurrences || null,
      recurrenceEndDate: selectedEntry.recurrenceEndDate || null,
      clearFields: clearFields.length > 0 ? clearFields : null,
      // applyToFollowing disabled — occurrences are independent
    };
  };

  // Execute the update API call
  const executeUpdate = async (payload) => {
    setLoading(true);
    try {
      await api.put(`asset-issues/update`, payload);
      fetchAssetIssueFromOrganization();
      handleModalClose();
    } catch (error) {
      setApiError(apiErrorMessage(error, t));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    setApiError('');

  const formErrors = validateForm();

  if (Object.keys(formErrors).length > 0) {
    setErrors(formErrors);
    return false;
  }

    // Edit scope dialog disabled — all occurrences are generated upfront and edited independently
    // const isPreventiveRecurring = selectedEntry?.workType === 'PREVENTIVE'
    //   && selectedEntry?.recurrenceType
    //   && selectedEntry?.recurrenceType !== 'NONE';
    //
    // if (isPreventiveRecurring) {
    //   setEditScopeDialog({ isOpen: true, pendingPayload: buildUpdatePayload() });
    //   return;
    // }

    await executeUpdate(buildUpdatePayload());
  }

  // Handle edit scope dialog selection (disabled — occurrences are independent)
  // const handleEditScopeConfirm = async (applyToFollowing) => {
  //   const payload = { ...editScopeDialog.pendingPayload, applyToFollowing };
  //   setEditScopeDialog({ isOpen: false, pendingPayload: null });
  //   await executeUpdate(payload);
  // };

  const handleTicketClick = async (ticketNumber) => {
    setTicketModal(prev => ({
      ...prev,
      isOpen: true,
      ticketData: null,
      loading: true
    }));

    try {
      const { data } = await api.get(`asset-issues/organization/filtered`, {
        params: { 
          ticket: ticketNumber
        }
      });
      
      setTicketModal(prev => ({
        ...prev,
        ticketData: data.response?.[0] || null,
        loading: false
      }));
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      setTicketModal(prev => ({ ...prev, loading: false }));
    }
  };

  const clearDateRange = () => {
    setFromDate(null);
    setToDate(null);
  };

  const fetchEnums = async () => {
    try {
      const { data } = await api.get("dynamic-enum/get-all");
      setEnumData(data.response || []);
    } catch (error) {
      console.error("Error fetching enums:", error);
    }
  };

  const fetchLocations = async () => {
    const { data } = await api.get(
      `organization-location/get-by-organization-id?organizationId=${organizationId}&count=9999&hierarchy=false`
    );
    setLocations(data.response[0]?.locations);
  };

  const fetchManufacturers = async () => {
    try {
      const { data } = await api.get("asset/get-manufacturer");
      setManufacturers(data.response?.manufacturers || []);
    } catch (error) {
      console.error("Error fetching manufacturers:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await api.get(`organization-category/getAll?organizationId=${organizationId}`);
      setCategories(data.response || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchVendors = async () => {
    try {
      const { data } = await api.get(`vendor/getAllVendors?count=9999`);
      setVendors(data.response || []);
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  // Fetches the full asset list used to populate the filter modal's Asset multi-select.
  // The backend groups assets by organization in data.response[0].assets.
  const fetchFilterAssets = async () => {
    try {
      const { data } = await api.get(`asset/all`, {
        params: { organizationId, count: 0, hierarchy: false }
      });
      setFilterAssets(data?.response?.[0]?.assets || []);
    } catch (error) {
      console.error("Error fetching assets for filter:", error);
    }
  };

  const handleImport = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleExport = () => {
    if (!transactions || transactions.length === 0) return;

    const dataToExport = transactions.map((transaction, index) => ({
      // Basic Info
      "#": index + 1,
      "Ticket Number": transaction?.ticketNumber || "",
      "Title": transaction?.title || "",
      "Description": transaction?.description || "",

      // Status & Priority
      "Status": transaction?.status || "",
      "Urgency Level": transaction?.urgencyLevel || "",
      "Impact On Care": transaction?.impactOnCare || "",
      "Work Type": transaction?.workType || "",

      // Dates
      "Issue Date": formatCreatedDate(transaction?.createdAt),
      "First Occurrence": formatCreatedDate(transaction?.firstOccurrence),
      "Due Date": transaction?.dueDate ? formatCreatedDate(transaction.dueDate) : "",
      "Resolution Date": formatCreatedDate(transaction?.resolvedAt),
      "Estimated Time": transaction?.estimatedTime || "",

      // People
      "Reported By": transaction?.reportedBy?.name || "",
      "Assigned Technician": transaction?.assignedTechnician?.map(t => t.name).join(", ") || "",
      "Resolved By": transaction?.resolvedBy?.name || "",

      // Asset Info
      "Asset ID": transaction?.asset?.id || "",
      "Asset Name": transaction?.asset?.assetName || "",
      "Asset Types": transaction?.asset?.assetTypes?.map(at => at.name).join(', ') || "",
      "Serial Number": transaction?.asset?.serialNumber || "",
      "Manufacturer": transaction?.asset?.manufacturer || "",
      "Model": transaction?.asset?.model || "",

      // Location
      "Organization": transaction?.organization?.name || "",
      "Location": transaction?.asset?.currentLocation || transaction?.locationName || "",

      // Issue Details
      "Issue Type": Array.isArray(transaction?.issueType)
        ? transaction.issueType.join(", ")
        : transaction?.issueType || "",
      "Symptoms": Array.isArray(transaction?.symptomsTags)
        ? transaction.symptomsTags.join(", ")
        : transaction?.symptomsTags || "",
      "Error Code": transaction?.errorCode || "",
      "Usage Status": transaction?.usageStatus || "",

      // Resolution
      "Resolution Notes": transaction?.resolutionNotes || "",
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Reactive Work Orders");

    XLSX.writeFile(
      wb,
      `reactive_work_orders_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  // Fixed sorting function - now sorts the entire dataset consistently
  const sortTransactions = useCallback((data) => {
    if (!data || data.length === 0) return data;
    
    return [...data].sort((a, b) => {
      let valueA, valueB;
      
      switch(sortCriteria.field) {
        case 'createdAt':
        case 'dueDate':
          valueA = new Date(a[sortCriteria.field] || 0);
          valueB = new Date(b[sortCriteria.field] || 0);
          break;
        case 'urgencyLevel':
          const priorityOrder = { 'LOW': 1, 'MEDIUM': 2, 'HIGH': 3 };
          valueA = priorityOrder[a[sortCriteria.field]] || 0;
          valueB = priorityOrder[b[sortCriteria.field]] || 0;
          break;
        case 'ticketNumber':
          // Extract numeric part from ticket number for proper sorting
          valueA = parseInt(a[sortCriteria.field]?.replace(/\D/g, '') || '0');
          valueB = parseInt(b[sortCriteria.field]?.replace(/\D/g, '') || '0');
          break;
        case 'assetName':
          valueA = a.asset?.assetName?.toLowerCase() || '';
          valueB = b.asset?.assetName?.toLowerCase() || '';
          break;
        case 'status':
          valueA = a[sortCriteria.field]?.toLowerCase() || '';
          valueB = b[sortCriteria.field]?.toLowerCase() || '';
          break;
        default:
          valueA = a[sortCriteria.field] || '';
          valueB = b[sortCriteria.field] || '';
          if (typeof valueA === 'string') valueA = valueA.toLowerCase();
          if (typeof valueB === 'string') valueB = valueB.toLowerCase();
      }
      
      if (sortCriteria.direction === 'asc') {
        return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
      } else {
        return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
      }
    });
  }, [sortCriteria]);

const fetchAssetIssueFromOrganization = useCallback(async () => {
  if (!organizationId) return;

  setLoading(true);
  try {
    const { assetIds, locationIds, assignedTeamId, ...scalarFilters } = filters;
    const params = {
      organizationId,
      count,
      ...scalarFilters,
    };
    // Spring @RequestParam List<Long> accepts a comma-separated string.
    if (Array.isArray(assetIds) && assetIds.length > 0) params.assetIds = assetIds.join(",");
    if (Array.isArray(locationIds) && locationIds.length > 0) params.locationIds = locationIds.join(",");
    // Slice 6 — assignedTeamId is a Long; only send when set so the backend
    // doesn't try to parse an empty string.
    if (assignedTeamId) params.assignedTeamId = assignedTeamId;
    // Work-type view: ALL means no work-type filter at all.
    if (params.workType === 'ALL') delete params.workType;

    // Clear any existing status filters first
    delete params.status;
    delete params.notStatus;

    // Add status filter based on active tab
    if (activeTab === 'resolved') {
      params.status = 'RESOLVED';
    } else {
      // For todo tab, exclude resolved tickets
      params.notStatus = 'RESOLVED';
    }

    if (fromDate) params.fromDate = formatDateTimeForAPI(fromDate);
    if (toDate) params.toDate = formatDateTimeForAPI(toDate);
    params.search = searchTerm;

    // Active shortcut overrides any conflicting status/priority/date filters
    const shortcutParams = buildShortcutParams(activeShortcut, currentUserId);
    if (shortcutParams) {
      Object.assign(params, shortcutParams);
    }

    const { data } = await api.get(`asset-issues/organization/filtered`, { params });

    const sortedData = sortTransactions(data.response || []);

    // Reset transactions for initial load (count === 10) or when switching tabs/filters
    if (count === 10) {
      setTransactions(sortedData);
    } else {
      // For load more, only add new items that aren't already in the list
      setTransactions((prev) => {
        const existingIds = new Set(prev.map(item => item.id));
        const newItems = sortedData.filter(item => !existingIds.has(item.id));
        return [...prev, ...newItems];
      });
    }

    setHasMore(data.response && data.response.length === count);
  } catch (error) {
    console.error("Error fetching Work Orders:", error);
  } finally {
    setLoading(false);
  }
}, [organizationId, count, filters, searchTerm, fromDate, toDate, sortTransactions, activeTab, activeShortcut, currentUserId]);

  // Re-sort when sort criteria changes
  useEffect(() => {
    if (transactions.length > 0) {
      setTransactions(prevTransactions => sortTransactions(prevTransactions));
    }
  }, [sortCriteria, sortTransactions]);

  // Auto-open ticket detail when navigated from notification with ?ticket= param
  useEffect(() => {
    const ticketParam = searchParams.get('ticket');
    if (ticketParam) {
      handleTicketClick(ticketParam);
      // Clear only the ticket param so it doesn't re-open on re-renders — keep the
      // shortcut / work-type view the user arrived with.
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('ticket');
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams]);

  // Calendar data fetching — only work orders, using dueDate
  const fetchCalendarData = useCallback(async () => {
    if (!organizationId) return;
    setCalendarLoading(true);
    try {
      const year = calendarDate.getFullYear();
      const month = calendarDate.getMonth();
      const calFromDate = formatDateTimeForAPI(new Date(year, month, 1));
      const calToDate = formatDateTimeForAPI(new Date(year, month + 1, 0, 23, 59, 59, 999));

      const params = { organizationId, fromDate: calFromDate, toDate: calToDate, notStatus: 'RESOLVED' };
      // Same work-type view as the list (ALL = no work-type filter).
      if (filters.workType && filters.workType !== 'ALL') params.workType = filters.workType;

      // Active shortcut layers on top and can override notStatus / add status filters.
      const shortcutParams = buildShortcutParams(activeShortcut, currentUserId);
      if (shortcutParams) {
        // If the shortcut sets a specific status (e.g. RESOLVED for completed_last_7_days),
        // drop the default notStatus so it doesn't contradict.
        if (shortcutParams.status) delete params.notStatus;
        Object.assign(params, shortcutParams);
      }

      const { data } = await api.get('asset-issues/organization/filtered', { params });

      const workOrders = (data?.response || []).map(item => ({
        ...item,
        type: 'work-order',
        eventTitle: item.title || t('shared.work_orders', 'Work Order'),
        eventDate: item.dueDate || item.requestDate
      }));

      setCalendarEvents(workOrders);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      setCalendarEvents([]);
    } finally {
      setCalendarLoading(false);
    }
  }, [organizationId, calendarDate, t, activeShortcut, currentUserId, filters.workType]);

  useEffect(() => {
    if (viewMode === 'calendar' && organizationId) {
      fetchCalendarData();
    }
  }, [viewMode, fetchCalendarData, organizationId]);

  const handleCalendarEventClick = (event) => {
    if (event.ticketNumber) {
      handleTicketClick(event.ticketNumber);
    }
  };

  const loadMore = () => {
    setCount(prevCount => prevCount + 10);
  };

  const handleModalClose = () => {
    setmodal_large(false);
    setSelectedEntry(null);
    setOriginalEntry(null);
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setTransactions([]);
    setCount(10);
    setHasMore(true);

    if (term === "") {
      fetchAssetIssueFromOrganization();
    }
  };

  const renderSelectOptions = (enumName) => {
    const values = getEnumValues(enumName);
    const translations = reactiveEnumTranslations(t);
    return (
      <>
        {values.map((value) => {
          const label = translations[value] || value.replace(/_/g, ' ');
          return (
            <option key={value} value={value}>
              {label}
            </option>
          );
        })}
      </>
    );
  };

  useEffect(() => {
    fetchEnums();
    fetchTechnicians();
    fetchManufacturers();
    fetchTeamsForFilter();
    if (organizationId) {
      fetchLocations();
      fetchCategories();
      fetchVendors();
      fetchFilterAssets();
    }
  }, []);

  useEffect(() => {
    if (organizationId) {
      setTransactions([]);
      setCount(10);
      setHasMore(true);
      fetchAssetIssueFromOrganization();
    }
  }, [filters, searchTerm, organizationId, fromDate, toDate, activeTab]);

  useEffect(() => {
    fetchAssetIssueFromOrganization();
  }, [fetchAssetIssueFromOrganization, count]);


  // Use transactions directly since they're already sorted
  const sortedTransactions = transactions;

  return (
    <div className="page-content mt-4">
      <div className="mb-4 d-flex align-items-center gap-3">
        <motion.div
          className="mb-4 d-flex align-items-center gap-3 w-100"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
                {/* Add Work Order Button */}
      <PermissionGuard permission="service:create">
        <button
          className="btn btn-success"
          onClick={() => tog_large()}
          title={t("business.reactive.add_new_work_order", "Add new work order")}
        >
          <FeatherIcon icon="plus" size={18} className="me-1" />
          {t("shared.create_work_order", "Create Work Order")}
        </button>
      </PermissionGuard>

          {viewMode === 'list' && (
          <div
            className="ms-auto"
            style={{ minWidth: "400px", flexGrow: 1, maxWidth: "500px" }}
          >
            <Input
              type="text"
              placeholder={t("business.reactive.search_work_orders", "Search work orders...")}
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          )}
        </motion.div>
      </div>
      <WorkOrderShortcuts
        organizationId={organizationId}
        activeShortcut={activeShortcut}
        onShortcutClick={handleShortcutClick}
        t={t}
      />
      {activeShortcut && (
        <div className="d-flex align-items-center gap-2 mb-2">
          <Badge color="info" pill className="px-3 py-2 d-flex align-items-center gap-2">
            <FeatherIcon icon="filter" size={12} />
            {t("business.reactive.shortcut.active_chip", "Showing")}:&nbsp;
            <span className="fw-semibold">
              {(() => {
                const sc = WORK_ORDER_SHORTCUTS.find((s) => s.key === activeShortcut);
                return sc ? t(sc.labelKey, sc.fallback) : activeShortcut;
              })()}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-link text-white p-0 ms-1"
              onClick={() => handleShortcutClick(null)}
              title={t("shared.clear", "Clear")}
              style={{ lineHeight: 1 }}
            >
              <FeatherIcon icon="x" size={14} />
            </button>
          </Badge>
        </div>
      )}
      <Card>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 px-3 pt-3">
        <Nav pills className="justify-content-start gap-2">
          <NavItem>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <NavLink
                style={{ cursor: 'pointer' }}
                className={`tab-btn ${activeTab === 'todo' && viewMode === 'list' ? 'active' : ''}`}
                onClick={() => toggleTab('todo')}
              >
                <FeatherIcon icon="list" className="me-1" />
                {t("business.reactive.to_do", "To Do")}
              </NavLink>
            </motion.div>
          </NavItem>
          <NavItem>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <NavLink
                style={{ cursor: 'pointer' }}
                className={`tab-btn ${activeTab === 'resolved' && viewMode === 'list' ? 'active' : ''}`}
                onClick={() => toggleTab('resolved')}
              >
                <FeatherIcon icon="check-circle" className="me-1" />
                {t("shared.resolved", "Resolved")}
              </NavLink>
            </motion.div>
          </NavItem>
          <NavItem>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <NavLink
                style={{ cursor: 'pointer' }}
                className={`tab-btn ${viewMode === 'calendar' ? 'active' : ''}`}
                onClick={() => setViewMode('calendar')}
              >
                <FeatherIcon icon="calendar" className="me-1" />
                {t("shared.calendar_view", "Calendar")}
              </NavLink>
            </motion.div>
          </NavItem>
        </Nav>
        <div
          className="btn-group btn-group-sm"
          role="group"
          aria-label={t("shared.work_type", "Work Type")}
        >
          {workOrderViewOptions(t).map((option) => (
            <React.Fragment key={option.value}>
              <input
                type="radio"
                className="btn-check"
                name="workOrderView"
                id={`workOrderView-${option.value}`}
                autoComplete="off"
                checked={filters.workType === option.value}
                onChange={() => handleWorkTypeViewChange(option.value)}
              />
              <label className="btn btn-outline-primary" htmlFor={`workOrderView-${option.value}`}>
                {option.label}
              </label>
            </React.Fragment>
          ))}
        </div>
        </div>

      {viewMode === 'calendar' ? (
        <WorkOrderCalendarGrid
          allEvents={calendarEvents}
          loading={calendarLoading}
          currentDate={calendarDate}
          setCurrentDate={setCalendarDate}
          onEventClick={handleCalendarEventClick}
          t={t}
        />
      ) : (
      <>
       <CardHeader>
  <div className="d-flex justify-content-between align-items-center">
    <h3 className="card-title text-success mb-0">{t("shared.work_orders", "Work Orders")}</h3>
    <div className="d-flex align-items-center gap-2">
      <button
        className={`btn p-2 ${fromDate ? 'btn-soft-primary' : 'btn-soft-secondary'}`}
        onClick={() => setDateRangeModal(true)}
        title={t("shared.date_range_filter", "Date range filter")}
      >
        <FeatherIcon icon="calendar" size={18} />
        {fromDate && (
          <span className="ms-1">
            {formatCreatedDate(fromDate)} - {toDate ? formatCreatedDate(toDate) : '...'}
          </span>
        )}
      </button>

      <button
        className="btn btn-soft-primary p-2"
        onClick={() => setSortModal(true)}
        title={t("business.reactive.sort_work_orders", "Sort work orders")}
      >
       <MdSort size={20} />
      </button>

      <button
        className="btn btn-soft-primary p-2"
        onClick={() => setFilterModal(true)}
        title={t("shared.filter_assets", "Filter Assets")}
      >
        <FeatherIcon icon="filter" size={18} />
      </button>
      <button
        className="btn btn-soft-info p-2"
        onClick={() => setFileModal(true)}
        title={t("business.reactive.import_export", "Import/Export Work Orders")}
      >
        <FeatherIcon icon="file-text" size={18} />
      </button>
    </div>
  </div>
</CardHeader>
        <TabContent activeTab={activeTab}>
          <TabPane tabId="todo">
            <CardBody>
              <div
                className="table-responsive"
                style={{
                  overflowX: "auto",
                  whiteSpace: "nowrap",
                  maxHeight: "calc(100vh - 300px)",
                  overflowY: "auto",
                }}
              >
                <table
                  className="table table-hover mb-0"
                  style={{
                    borderSpacing: "0.1rem",
                    borderCollapse: "separate",
                  }}
                >
                  <thead
                    style={{
                      position: "sticky",
                      top: 0,
                      background: "white",
                      zIndex: 1,
                    }}
                  >
                    <tr>
                      <th>#</th>
                      <th>{t("shared.title", "Title")}</th>
                      <th>{t("shared.asset", "Asset")}</th>
                      <th>{t("shared.location", "Location")}</th>
                      <th>{t("shared.planning", "Planning")}</th>
                      <th
                        onClick={() => {
                          setSortCriteria(prev => ({
                            field: 'dueDate',
                            direction: prev.field === 'dueDate' && prev.direction === 'desc' ? 'asc' : 'desc'
                          }));
                        }}
                        className="cursor-pointer"
                      >
                        {t("shared.due_date", "Due Date")}
                        {sortCriteria.field === 'dueDate' && (
                          <FeatherIcon
                            icon={sortCriteria.direction === 'asc' ? 'arrow-up' : 'arrow-down'}
                            size={14}
                            className="ms-1"
                          />
                        )}
                      </th>
                      <th>{t("shared.work_type", "Work Type")}</th>
                      <th>{t("shared.status", "Status")}</th>
                      <th>{t("shared.priority", "Priority")}</th>
                      <th>{t("shared.first_occurrence", "First Occurrence")}</th>
                      <th>{t("shared.reported_by", "Reported By")}</th>
                      <th>{t("shared.assigned_technician", "Assigned Technician")}</th>
                      <th>{t("shared.estimated_time", "Estimated Time")}</th>
                      <th>{t("shared.ticket_number", "Ticket Number")}</th>
                      <th>{t("shared.delete", "Delete")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <AnimatePresence>
                      {sortedTransactions?.map((transaction, ind) => (
                        <motion.tr
                          key={transaction?.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.3 }}
                        >
                          <th scope="row">{ind + 1}</th>
                          <td>
                            <span
                              className="text-primary cursor-pointer hover-underline text-truncate d-inline-block"
                              onClick={() => handleTicketClick(transaction.ticketNumber)}
                              style={{ cursor: 'pointer', maxWidth: '200px' }}
                              title={transaction?.title || ''}
                            >
                              {transaction?.title || t("shared.n_a", 'N/A')}
                            </span>
                          </td>
                          <td>
                          {transaction?.asset ? (
                            <span
                                className="text-primary cursor-pointer hover-underline"
                                onClick={() => handleAssetClick(transaction?.asset?.id, transaction?.asset)}
                                style={{ cursor: 'pointer' }}
                            >
                                {transaction?.asset?.assetName}
                            </span>
                          ) : (
                            <span className="text-muted">{t("business.reactive.no_asset", "No Asset")}</span>
                          )}
                          </td>
                          <td>
                            {(transaction?.asset?.currentLocation || transaction?.locationName) ? (
                              <span
                                className="text-primary cursor-pointer hover-underline"
                                onClick={() => handleDetailsClick('location', transaction?.asset?.currentLocation || transaction?.locationName, transaction.organization)}
                                style={{ cursor: 'pointer' }}
                              >
                                {transaction?.asset?.currentLocation || transaction?.locationName}
                              </span>
                            ) : (
                              <span className="text-muted">{t("shared.n_a", "N/A")}</span>
                            )}
                          </td>
                          <td>
                            {transaction?.dueDate ? (() => {
                              const dueDate = new Date(transaction.dueDate);
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);

                              if (dueDate < today) {
                                return (
                                  <Badge color="danger" pill className="py-1 px-2">
                                    <FeatherIcon icon="alert-triangle" size={12} className="me-1" />
                                    {t("shared.overdue", "Overdue")}
                                  </Badge>
                                );
                              } else {
                                return (
                                  <Badge color="success" pill className="py-1 px-2">
                                    <FeatherIcon icon="check-circle" size={12} className="me-1" />
                                    {t("business.reactive.on_track", "On track")}
                                  </Badge>
                                );
                              }
                            })() : null}
                          </td>
                          <td>{transaction?.dueDate ? formatCreatedDate(transaction.dueDate) : ''}</td>
                          <td>
                            <Badge color={getWorkTypeBadgeColor(transaction?.workType)} pill className="py-1 px-2 text-uppercase">
                              {t(`shared.${(transaction?.workType || 'OTHER').toLowerCase()}`)}
                            </Badge>
                          </td>
                          <td>
                          <Dropdown
                            isOpen={dropdownOpen[transaction?.ticketNumber] || false}
                            toggle={() => toggleDropdown(transaction?.ticketNumber)}
                            direction="down"
                          >
                            <DropdownToggle
                              tag="div"
                              className="d-inline-block"
                              style={{ cursor: 'pointer' }}
                            >
                              <WorkOrderStatusBadge status={transaction?.status} />
                            </DropdownToggle>
                            <DropdownMenu container="body">
                              {reactiveStatusOptions(t).map((option) => (
                                <DropdownItem
                                  key={option.value}
                                  className="d-flex align-items-center gap-2"
                                  onClick={() => onStatusChange(transaction, option.value)}
                                >
                                  <FeatherIcon icon={option.icon} size={14} />
                                  {option.label}
                                </DropdownItem>
                              ))}
                            </DropdownMenu>
                          </Dropdown>
                        </td>
                     <td>
                      <Dropdown
                        isOpen={dropdownOpen[`priority-${transaction.id}`] || false}
                        toggle={() => toggleDropdown(`priority-${transaction.id}`)}
                        direction="down"
                      >
                        <DropdownToggle tag="div" style={{ cursor: "pointer" }}>
                          <span
                            className={`badge rounded-pill py-1 px-2 text-uppercase ${
                              urgencyColors[transaction?.urgencyLevel] || "bg-secondary"
                            }`}
                          >
                            {transaction?.urgencyLevel ? t(`shared.${transaction?.urgencyLevel.toLowerCase()}`) : ""}
                          </span>
                        </DropdownToggle>

                        <DropdownMenu container="body">
                          {[ "HIGH", "MEDIUM", "LOW"].map((level) => (
                            <DropdownItem
                              key={level}
                              onClick={() => handlePriorityChange(transaction, level)}
                            >
                              <span
                                className={`badge rounded-pill py-1 px-2 ${urgencyColors[level]}`}
                              >
                                {t(`shared.${level.toLowerCase()}`, level.charAt(0) + level.slice(1).toLowerCase())}
                              </span>
                            </DropdownItem>
                          ))}
                        </DropdownMenu>
                      </Dropdown>
                    </td>
                          <td>{formatCreatedDate(transaction?.firstOccurrence)}</td>
                          <td>
                            {transaction?.reportedBy?.id ? (
                              <span
                                className="text-primary cursor-pointer hover-underline"
                                onClick={() => handleReportedByClick(transaction.reportedBy.id)}
                                style={{ cursor: 'pointer' }}
                              >
                                {transaction?.reportedBy?.name}
                              </span>
                            ) : (
                              transaction?.reportedBy?.name || t("shared.n_a", 'N/A')
                            )}
                          </td>
                          <td>
                            {((transaction?.assignedTechnician && transaction.assignedTechnician.length > 0)
                              || (transaction?.assignedTeams && transaction.assignedTeams.length > 0)) ? (
                              <div className="d-flex flex-wrap gap-1">
                                {transaction.assignedTechnician?.map((technician, index) => (
                                  <span
                                    key={`u-${technician.id || index}`}
                                    className="badge bg-primary text-white cursor-pointer"
                                    onClick={() => handleTechnicianClick(technician.id)}
                                    style={{ cursor: 'pointer', fontSize: '10px' }}
                                    title={t("business.reactive.click_to_view_details", "Click to view {{name}}'s details", { name: technician.name })}
                                  >
                                    <FeatherIcon icon="user" size={10} className="me-1" />
                                    {technician.name}
                                  </span>
                                ))}
                                {transaction.assignedTeams?.map((team, index) => (
                                  <span
                                    key={`t-${team.id || index}`}
                                    className="badge text-white"
                                    style={{
                                      fontSize: '10px',
                                      background: team.color || '#0E7C7B',
                                    }}
                                    title={t("teams.assignedTeamTooltip", "Team — {{count}} member(s) will be notified", { count: team.memberCount ?? 0 })}
                                  >
                                    <FeatherIcon icon="users" size={10} className="me-1" />
                                    {team.name}
                                  </span>
                                ))}
                              </div>
                            ) : transaction?.assignedTechnician?.id ? (
                              <span
                                className="text-primary cursor-pointer hover-underline"
                                onClick={() => handleTechnicianClick(transaction.assignedTechnician.id)}
                                style={{ cursor: 'pointer' }}
                              >
                                {transaction?.assignedTechnician?.name}
                              </span>
                            ) : (
                              <span className="text-muted small">{t("shared.not_assigned", "Not assigned")}</span>
                            )}
                          </td>
                          <td>
                            <span className="text-muted small">
                              {transaction?.estimatedTime ?
                                `${convertTimeFormatToHours(transaction.estimatedTime).toFixed(1)}h` :
                                t("shared.not_set", 'Not set')
                              }
                            </span>
                          </td>
                          <td>
                            <span
                              className="text-primary cursor-pointer hover-underline"
                              onClick={() => handleTicketClick(transaction.ticketNumber)}
                              style={{ cursor: 'pointer' }}
                            >
                              {transaction?.ticketNumber || t("shared.n_a", 'N/A')}
                            </span>
                          </td>
                          <td>
                            <PermissionGuard permission="service:delete">
                              <span
                                className="text-danger"
                                role="button"
                                onClick={() => {
                                  setSelectedEntry(transaction);
                                  setDeleteModal(true);
                                }}
                              >
                                <FeatherIcon icon="trash-2" />
                              </span>
                            </PermissionGuard>
                          </td>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>

                {loading && (
                  <div className="text-center py-3">
                    <Spinner color="primary" />
                  </div>
                )}

                <div className="d-flex justify-content-center mt-3">
                  {hasMore && sortedTransactions.length > 0 ? (
                    <Button
                      color="primary"
                      onClick={loadMore}
                      disabled={loading}
                    >
                      {loading ? t("shared.loading", 'Loading...') : t("shared.load", 'Load More')}
                    </Button>
                  ) : (
                    sortedTransactions.length > 0 && (
                      <div className="text-muted">{t("business.reactive.no_more_work_orders", "No more Work Orders to load")}</div>
                    )
                  )}
                </div>

                {sortedTransactions.length === 0 && !loading && (
                  <div className="text-center py-5 text-muted">
                    {searchTerm
                      ? t("business.reactive.no_matches", "No Work Orders match your search")
                      : (visibilityRestricted
                        ? t("teams.restrictVisibility.emptyWorkOrders", "No work orders in your team's scope.")
                        : t("business.reactive.no_work_orders", "No Work Orders found"))}
                  </div>
                )}
              </div>
            </CardBody>
          </TabPane>

          <TabPane tabId="resolved">
            <CardBody>
              <div
                className="table-responsive"
                style={{
                  overflowX: "auto",
                  whiteSpace: "nowrap",
                  maxHeight: "calc(100vh - 300px)",
                  overflowY: "auto",
                }}
              >
                <table
                  className="table table-hover mb-0"
                  style={{
                    borderSpacing: "0.1rem",
                    borderCollapse: "separate",
                  }}
                >
                  <thead
                    style={{
                      position: "sticky",
                      top: 0,
                      background: "white",
                      zIndex: 1,
                    }}
                  >
                    <tr>
                      <th>#</th>
                      <th>{t("shared.title", "Title")}</th>
                      <th>{t("shared.asset", "Asset")}</th>
                      <th>{t("shared.location", "Location")}</th>
                      <th>{t("shared.resolved_at", "Resolved At")}</th>
                      <th>{t("shared.work_type", "Work Type")}</th>
                      <th>{t("shared.status", "Status")}</th>
                      <th>{t("shared.priority", "Priority")}</th>
                      <th>{t("shared.first_occurrence", "First Occurrence")}</th>
                      <th>{t("shared.reported_by", "Reported By")}</th>
                      <th>{t("shared.assigned_technician", "Assigned Technician")}</th>
                      <th>{t("shared.ticket_number", "Ticket Number")}</th>
                      <th>{t("shared.delete", "Delete")}</th>
                      <th>{t("shared.created_at", "Created At")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <AnimatePresence>
                      {sortedTransactions?.map((transaction, ind) => (
                        <motion.tr
                          key={transaction?.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.3 }}
                        >
                          <th scope="row">{ind + 1}</th>
                          <td>
                            <span
                              className="text-primary cursor-pointer hover-underline text-truncate d-inline-block"
                              onClick={() => handleTicketClick(transaction.ticketNumber)}
                              style={{ cursor: 'pointer', maxWidth: '200px' }}
                              title={transaction?.title || ''}
                            >
                              {transaction?.title || t("shared.n_a", 'N/A')}
                            </span>
                          </td>
                          <td>
                          {transaction?.asset ? (
                            <span
                                className="text-primary cursor-pointer hover-underline"
                                onClick={() => handleAssetClick(transaction?.asset?.id, transaction?.asset)}
                                style={{ cursor: 'pointer' }}
                            >
                                {transaction?.asset?.assetName}
                            </span>
                          ) : (
                            <span className="text-muted">{t("business.reactive.no_asset", "No Asset")}</span>
                          )}
                          </td>
                          <td>
                            {(transaction?.asset?.currentLocation || transaction?.locationName) ? (
                              <span
                                className="text-primary cursor-pointer hover-underline"
                                onClick={() => handleDetailsClick('location', transaction?.asset?.currentLocation || transaction?.locationName, transaction.organization)}
                                style={{ cursor: 'pointer' }}
                              >
                                {transaction?.asset?.currentLocation || transaction?.locationName}
                              </span>
                            ) : (
                              <span className="text-muted">{t("shared.n_a", "N/A")}</span>
                            )}
                          </td>
                          <td>{transaction?.resolvedAt ? formatCreatedDate(transaction?.resolvedAt) : '-'}</td>
                          <td>
                            <Badge color={getWorkTypeBadgeColor(transaction?.workType)} pill className="py-1 px-2">
                              {t(`shared.${(transaction?.workType || 'OTHER').toLowerCase()}`)}
                            </Badge>
                          </td>
                          <td>
                            <WorkOrderStatusBadge status={transaction?.status} />
                          </td>
                      <td>
                        <span
                            className={`badge rounded-pill py-1 px-2 ${
                              urgencyColors[transaction?.urgencyLevel] || "bg-secondary"
                            }`}
                          >
                            {transaction?.urgencyLevel
                              ? transaction.urgencyLevel.replace(/_/g, " ")
                              : ""}
                          </span>
                    </td>
                          <td>{formatCreatedDate(transaction?.firstOccurrence)}</td>
                          <td>
                            {transaction?.reportedBy?.id ? (
                              <span
                                className="text-primary cursor-pointer hover-underline"
                                onClick={() => handleReportedByClick(transaction.reportedBy.id)}
                                style={{ cursor: 'pointer' }}
                              >
                                {transaction?.reportedBy?.name}
                              </span>
                            ) : (
                              transaction?.reportedBy?.name || t("shared.n_a", 'N/A')
                            )}
                          </td>
                          <td>
                            {((transaction?.assignedTechnician && transaction.assignedTechnician.length > 0)
                              || (transaction?.assignedTeams && transaction.assignedTeams.length > 0)) ? (
                              <div className="d-flex flex-wrap gap-1">
                                {transaction.assignedTechnician?.map((technician, index) => (
                                  <span
                                    key={`u-${technician.id || index}`}
                                    className="badge bg-primary text-white cursor-pointer"
                                    onClick={() => handleTechnicianClick(technician.id)}
                                    style={{ cursor: 'pointer', fontSize: '10px' }}
                                    title={t("business.reactive.click_to_view_details", "Click to view {{name}}'s details", { name: technician.name })}
                                  >
                                    <FeatherIcon icon="user" size={10} className="me-1" />
                                    {technician.name}
                                  </span>
                                ))}
                                {transaction.assignedTeams?.map((team, index) => (
                                  <span
                                    key={`t-${team.id || index}`}
                                    className="badge text-white"
                                    style={{
                                      fontSize: '10px',
                                      background: team.color || '#0E7C7B',
                                    }}
                                    title={t("teams.assignedTeamTooltip", "Team — {{count}} member(s) will be notified", { count: team.memberCount ?? 0 })}
                                  >
                                    <FeatherIcon icon="users" size={10} className="me-1" />
                                    {team.name}
                                  </span>
                                ))}
                              </div>
                            ) : transaction?.assignedTechnician?.id ? (
                              <span
                                className="text-primary cursor-pointer hover-underline"
                                onClick={() => handleTechnicianClick(transaction.assignedTechnician.id)}
                                style={{ cursor: 'pointer' }}
                              >
                                {transaction?.assignedTechnician?.name}
                              </span>
                            ) : (
                              <span className="text-muted small">{t("shared.not_assigned", "Not assigned")}</span>
                            )}
                          </td>
                          <td>
                            <span
                              className="text-primary cursor-pointer hover-underline"
                              onClick={() => handleTicketClick(transaction.ticketNumber)}
                              style={{ cursor: 'pointer' }}
                            >
                              {transaction?.ticketNumber || t("shared.n_a", 'N/A')}
                            </span>
                          </td>
                          <td>
                            <PermissionGuard permission="service:delete">
                              <span
                                className="text-danger"
                                role="button"
                                onClick={() => {
                                  setSelectedEntry(transaction);
                                  setDeleteModal(true);
                                }}
                              >
                                <FeatherIcon icon="trash-2" />
                              </span>
                            </PermissionGuard>
                          </td>
                          <td>{formatCreatedDate(transaction?.createdAt)}</td>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>

                {loading && (
                  <div className="text-center py-3">
                    <Spinner color="primary" />
                  </div>
                )}

                <div className="d-flex justify-content-center mt-3">
                  {hasMore && sortedTransactions.length > 0 ? (
                    <Button
                      color="primary"
                      onClick={loadMore}
                      disabled={loading}
                    >
                      {loading ? t("shared.loading", 'Loading...') : t("shared.load", 'Load More')}
                    </Button>
                  ) : (
                    sortedTransactions.length > 0 && (
                      <div className="text-muted">{t("business.reactive.no_more_resolved_work_orders", "No more resolved Work Orders to load")}</div>
                    )
                  )}
                </div>

                {sortedTransactions.length === 0 && !loading && (
                  <div className="text-center py-5 text-muted">
                    {searchTerm
                      ? t("business.reactive.no_resolved_matches", "No resolved Work Orders match your search")
                      : t("business.reactive.no_resolved_work_orders", "No resolved Work Orders found")}
                  </div>
                )}
              </div>
            </CardBody>
          </TabPane>
        </TabContent>
      </>
      )}
      </Card>

      {/* Sort Modal */}
      <Modal
        isOpen={sortModal}
        toggle={() => setSortModal(false)}
        centered
        className="fade-scale-modal"
        backdropClassName="modal-backdrop-blur"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.2 }}
        >
          <div className="modal-header text-white">
            <h5 className="modal-title fs-4">{t("business.reactive.sort_work_orders", "Sort Work Orders")}</h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={() => setSortModal(false)}
              aria-label={t("shared.close", "Close")}
            ></button>
          </div>

          <div className="modal-body p-4">
            <div className="mb-3">
              <label className="form-label fw-medium">{t("shared.sort_by", "Sort By")}</label>
              <div className="btn-group w-100" role="group">
                <input
                  type="radio"
                  className="btn-check"
                  name="sortField"
                  id="sortCreatedAt"
                  autoComplete="off"
                  checked={sortCriteria.field === 'createdAt'}
                  onChange={() => setSortCriteria({...sortCriteria, field: 'createdAt'})}
                />
                <label className="btn btn-outline-primary" htmlFor="sortCreatedAt">
                  {t("business.reactive.creation_date", "Creation Date")}
                </label>

                <input
                  type="radio"
                  className="btn-check"
                  name="sortField"
                  id="sortDueDate"
                  autoComplete="off"
                  checked={sortCriteria.field === 'dueDate'}
                  onChange={() => setSortCriteria({...sortCriteria, field: 'dueDate'})}
                />
                <label className="btn btn-outline-primary" htmlFor="sortDueDate">
                  {t("shared.due_date", "Due Date")}
                </label>

                <input
                  type="radio"
                  className="btn-check"
                  name="sortField"
                  id="sortPriority"
                  autoComplete="off"
                  checked={sortCriteria.field === 'urgencyLevel'}
                  onChange={() => setSortCriteria({...sortCriteria, field: 'urgencyLevel'})}
                />
                <label className="btn btn-outline-primary" htmlFor="sortPriority">
                  {t("shared.priority", "Priority")}
                </label>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-medium">{t("shared.sort_direction", "Sort Direction")}</label>
              <div className="btn-group w-100" role="group">
                <input
                  type="radio"
                  className="btn-check"
                  name="sortDirection"
                  id="sortAsc"
                  autoComplete="off"
                  checked={sortCriteria.direction === 'asc'}
                  onChange={() => setSortCriteria({...sortCriteria, direction: 'asc'})}
                />
                <label className="btn btn-outline-primary" htmlFor="sortAsc">
                  {t("shared.ascending", "Ascending")}
                </label>

                <input
                  type="radio"
                  className="btn-check"
                  name="sortDirection"
                  id="sortDesc"
                  autoComplete="off"
                  checked={sortCriteria.direction === 'desc'}
                  onChange={() => setSortCriteria({...sortCriteria, direction: 'desc'})}
                />
                <label className="btn btn-outline-primary" htmlFor="sortDesc">
                  {t("shared.descending", "Descending")}
                </label>
              </div>
            </div>
          </div>

          <div className="modal-footer bg-light border-top-0">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => {
                setSortCriteria({ field: 'createdAt', direction: 'desc' });
                setSortModal(false);
              }}
            >
              {t("shared.reset", "Reset")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setSortModal(false)}
            >
              {t("shared.apply_sort", "Apply Sort")}
            </button>
          </div>
        </motion.div>
      </Modal>

      {/* Date Range Modal */}
      <Modal
        isOpen={dateRangeModal}
        toggle={() => setDateRangeModal(false)}
        centered
        className="fade-scale-modal"
        backdropClassName="modal-backdrop-blur"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.2 }}
        >
          <div className="modal-header text-white">
            <h5 className="modal-title fs-4">{t("shared.select_date_range", "Select Date Range")}</h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={() => setDateRangeModal(false)}
              aria-label={t("shared.close", "Close")}
            ></button>
          </div>

          <div className="modal-body p-4">
            <Row className="g-3">
              <Col md={6}>
                <div className="form-group">
                  <label htmlFor="fromDate" className="form-label fw-medium">
                    {t("shared.from_date", "From Date")}
                  </label>
                  <DatePicker
                    id="fromDate"
                    selected={fromDate}
                    onChange={(date) => setFromDate(date)}
                    selectsStart
                    startDate={fromDate}
                    endDate={toDate}
                    className="form-control"
                    dateFormat="MMMM d, yyyy"
                    placeholderText={t('shared.select_start_date', 'Select start date')}
                    isClearable
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="form-group">
                  <label htmlFor="toDate" className="form-label fw-medium">
                    {t("shared.to_date", "To date")}
                  </label>
                  <DatePicker
                    id="toDate"
                    selected={toDate}
                    onChange={(date) => setToDate(date)}
                    selectsEnd
                    startDate={fromDate}
                    endDate={toDate}
                    minDate={fromDate}
                    className="form-control"
                    dateFormat="MMMM d, yyyy"
                    placeholderText={t('shared.select_end_date', 'Select end date')}
                    isClearable
                    disabled={!fromDate}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                  />
                </div>
              </Col>
            </Row>
          </div>

          <div className="modal-footer bg-light border-top-0">
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={() => {
                clearDateRange();
                setDateRangeModal(false);
              }}
            >
              {t("shared.clear_dates", "Clear Dates")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setDateRangeModal(false)}
              disabled={fromDate && !toDate}
            >
              {t("shared.apply_dates", "Apply Dates")}
            </button>
          </div>
        </motion.div>
      </Modal>

      {/* Filter Modal */}
      <Modal
        isOpen={filterModal}
        toggle={() => setFilterModal(false)}
        size="lg"
        className="fade-scale-modal"
        backdropClassName="modal-backdrop-blur"
        centered
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.2 }}
        >
          <div className="modal-header text-white">
            <h5 className="modal-title fs-4">{t("business.reactive.filter_work_orders", "Filter Work Orders")}</h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={() => setFilterModal(false)}
              aria-label={t("shared.close", "Close")}
            ></button>
          </div>

          <div className="modal-body p-4">
            <Row className="mb-4 g-3">
              <Col md={6}>
                <div className="form-group">
                  <label htmlFor="filterStatus" className="form-label fw-medium">
                    {t("shared.status", "Status")}
                  </label>
                  <select
                    id="filterStatus"
                    className="form-select"
                    value={filters.status}
                    onChange={(e) =>
                      setFilters({ ...filters, status: e.target.value })
                    }
                  >
                    <option value="">{t("shared.all_statuses", "All Statuses")}</option>
                    <option value="SUBMITTED">{t("shared.open", "Open")}</option>
                    <option value="IN_PROGRESS">{t("shared.in_progress", "In Progress")}</option>
                    <option value="RESOLVED">{t("shared.resolved", "Resolved")}</option>
                    <option value="ON_HOLD">{t("shared.on_hold", "On Hold")}</option>
                  </select>
                </div>
              </Col>
              <Col md={6}>
                <div className="form-group">
                  <label htmlFor="filterPriority" className="form-label fw-medium">
                    {t("shared.priority", "Priority")}
                  </label>
                  <select
                    id="filterPriority"
                    className="form-select"
                    value={filters.urgencyLevel}
                    onChange={(e) =>
                      setFilters({ ...filters, urgencyLevel: e.target.value })
                    }
                  >
                    {renderSelectOptions("urgencyLevel")}
                  </select>
                </div>
              </Col>
            </Row>

            <Row className="mb-4 g-3">
              <Col md={6}>
                <div className="form-group">
                  <label htmlFor="filterCategory" className="form-label fw-medium">
                    {t("shared.category", "Category")}
                  </label>
                  <select
                    id="filterCategory"
                    className="form-select"
                    value={filters.categoryId}
                    onChange={(e) =>
                      setFilters({ ...filters, categoryId: e.target.value })
                    }
                  >
                    <option value="">{t("business.reactive.filter.all_categories", "All Categories")}</option>
                    {categories?.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </Col>
            </Row>

            <Row className="mb-4 g-3">
              <Col md={6}>
                <div className="form-group">
                  <label htmlFor="filterImpactOnCare" className="form-label fw-medium">
                    {t("shared.impact", "Impact")}
                  </label>
                  <select
                    id="filterImpactOnCare"
                    className="form-select"
                    value={filters.impactOnCare}
                    onChange={(e) =>
                      setFilters({ ...filters, impactOnCare: e.target.value })
                    }
                  >
                    <option value="">{t("shared.all", "All")}</option>
                    {renderSelectOptions("impactOnCare")}
                  </select>
                </div>
              </Col>
              <Col md={6}>
                <div className="form-group">
                  <label htmlFor="filterUsageStatus" className="form-label fw-medium">
                    {t("shared.usage_status", "Usage Status")}
                  </label>
                  <select
                    id="filterUsageStatus"
                    className="form-select"
                    value={filters.usageStatus}
                    onChange={(e) =>
                      setFilters({ ...filters, usageStatus: e.target.value })
                    }
                  >
                    <option value="">{t("shared.all", "All")}</option>
                    {renderSelectOptions("usageStatus")}
                  </select>
                </div>
              </Col>
            </Row>

            <Row className="mb-4 g-3">
              <Col md={6}>
                <div className="form-group">
                  <label htmlFor="filterTeam" className="form-label fw-medium">
                    {t("teams.title", "Teams")}
                  </label>
                  <select
                    id="filterTeam"
                    className="form-select"
                    value={filters.assignedTeamId}
                    onChange={(e) =>
                      setFilters({ ...filters, assignedTeamId: e.target.value })
                    }
                  >
                    <option value="">{t("teams.filter.allTeams", "All teams")}</option>
                    {orgTeamsForFilter.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                        {team.memberCount != null
                          ? ` (${team.memberCount})`
                          : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </Col>
            </Row>

            <Row className="mb-4 g-3">
              <Col md={6}>
                <div className="form-group">
                  <label htmlFor="filterAsset" className="form-label fw-medium">
                    {t("shared.asset", "Asset")}
                  </label>
                  <Select
                    inputId="filterAsset"
                    isMulti
                    isClearable
                    isSearchable
                    placeholder={t("business.reactive.filter.all_assets", "All Assets")}
                    options={(filterAssets || []).map((a) => ({
                      value: a.id,
                      label: a.assetName || a.serialNumber || `#${a.id}`,
                    }))}
                    value={(filterAssets || [])
                      .filter((a) => filters.assetIds?.includes(a.id))
                      .map((a) => ({ value: a.id, label: a.assetName || a.serialNumber || `#${a.id}` }))}
                    onChange={(selected) =>
                      setFilters({
                        ...filters,
                        assetIds: (selected || []).map((s) => s.value),
                      })
                    }
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="form-group">
                  <label htmlFor="filterLocation" className="form-label fw-medium">
                    {t("shared.location", "Location")}
                  </label>
                  <Select
                    inputId="filterLocation"
                    isMulti
                    isClearable
                    isSearchable
                    placeholder={t("shared.all_locations", "All Locations")}
                    options={(locations || []).map((l) => ({
                      value: l.id,
                      label: l.locationName,
                    }))}
                    value={(locations || [])
                      .filter((l) => filters.locationIds?.includes(l.id))
                      .map((l) => ({ value: l.id, label: l.locationName }))}
                    onChange={(selected) =>
                      setFilters({
                        ...filters,
                        locationIds: (selected || []).map((s) => s.value),
                      })
                    }
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  />
                </div>
              </Col>
            </Row>
          </div>

          <div className="modal-footer bg-light border-top-0">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => {
                // The work-type view is a view mode, not a filter — "Clear All" keeps it.
                setFilters((prev) => ({
                  status: "",
                  urgencyLevel: "",
                  workType: prev.workType,
                  categoryId: "",
                  assignedTeamId: "",
                  impactOnCare: "",
                  usageStatus: "",
                  assetIds: [],
                  locationIds: [],
                }));
                setFilterModal(false);
              }}
            >
              {t("shared.clear_all", "Clear All")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setFilterModal(false);
              }}
            >
              {t("shared.apply_filters", "Apply Filters")}
            </button>
          </div>
        </motion.div>
      </Modal>

      {/* Location Modal — dedicated detail view for locations */}
      <LocationModal
        isOpen={locationModal.isOpen}
        toggle={() => setLocationModal(prev => ({ ...prev, isOpen: false }))}
        title={locationModal.title}
        mainData={locationModal.mainData}
        relatedData={locationModal.relatedData}
        loading={locationModal.loading}
        locationData={locationModal.locationData}
        formatCreatedDate={formatCreatedDate}
        onTicketClick={handleTicketClick}
      />

      <AssetModal
        isOpen={assetModal.isOpen}
        toggle={() => setAssetModal(prev => ({ ...prev, isOpen: false }))}
        userData={assetModal.userData}
        loading={assetModal.loading}
        formatCreatedDate={formatCreatedDate}
        onTicketClick={handleTicketClick}
        fetchUserWorkOrders={handleReportedByClick}
        />

      {/* Ticket Details Modal */}
      <TicketDetailsModal
      isOpen={ticketModal.isOpen}
      enumData={enumData}
      toggle={() => setTicketModal(prev => ({ ...prev, isOpen: false }))}
      ticketNumber={ticketModal.ticketData?.ticketNumber}
      loading={ticketModal.loading}
      formatCreatedDate={formatCreatedDate}
      onStatusChange={onStatusChange}
      fetchAssetIssueFromOrganization={fetchAssetIssueFromOrganization}
      workType="REACTIVE"
      onAssetClick={handleAssetClick}
      onEdit={(ticketData) => {
        setTicketModal(prev => ({ ...prev, isOpen: false }));
        tog_large(ticketData);
      }}
      />

      <ReportedByModal
        isOpen={reportedByModal.isOpen}
        toggle={() => setReportedByModal(prev => ({ ...prev, isOpen: false }))}
        userData={reportedByModal.userData}
        loading={reportedByModal.loading}
        formatCreatedDate={formatCreatedDate}
        onTicketClick={handleTicketClick}
      />

      <FileModal
        title={t("shared.work_orders", "Work Orders")}
        isOpen={fileModal}
        toggle={() => setFileModal(false)}
        data={transactions}
        onImport={handleImport}
        onExport={handleExport}
        importLabel={t("shared.import_work_orders_from_excel", "Import Work Orders from Excel")}
        importNeed={false}
        exportLabel={t("shared.export_work_orders_to_excel", "Export Work Orders to Excel")}
      />
      <TechnicianModal
          isOpen={technicianModal.isOpen}
          toggle={() => setTechnicianModal(prev => ({ ...prev, isOpen: false }))}
          technicianData={technicianModal.technicianData}
          loading={technicianModal.loading}
          formatCreatedDate={formatCreatedDate}
          onTicketClick={handleTicketClick}
        />

      {/* Assign Technician + Team Modal */}
      <AssignTechnicianModal
        isOpen={assignTechnicianModal.isOpen}
        toggle={() => setAssignTechnicianModal({
          isOpen: false,
          ticketId: null,
          pendingTransaction: null
        })}
        ticketId={assignTechnicianModal.ticketId}
        assignedTechnician={assignTechnicianModal.pendingTransaction?.assignedTechnician}
        assignedTeams={assignTechnicianModal.pendingTransaction?.assignedTeams}
        onAssign={handleAssignTechnician}
        isPendingStatus={true}
      />
      {/* Add/Edit Modal */}
<Modal size="lg" isOpen={modal_large} toggle={tog_large}>
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.9 }}
    transition={{ duration: 0.2 }}
  >
    <div className="modal-header text-white">
      <h5 className="modal-title fs-4">
        {selectedEntry?.id ? t('shared.edit_work_order', 'Edit Work Order') : t('shared.add_work_order', 'Add Work Order')}
      </h5>
      <button
        type="button"
        className="btn-close btn-close-white"
        onClick={handleModalClose}
        aria-label={t("shared.close", "Close")}
      ></button>
    </div>
    
    <div className="modal-body p-4">
      <WorkOrderForm
        formData={selectedEntry}
        onChange={(updates) => setSelectedEntry(prev => ({ ...prev, ...updates }))}
        errors={errors}
        apiError={apiError}
        locations={locations}
        technicians={technicians}
        teams={orgTeamsForFilter}
        categories={categories}
        vendors={vendors}
        enumData={enumData}
        isEditMode={!!selectedEntry?.id}
        organizationId={organizationId}
        manufacturers={manufacturers}
      />
    </div>

    <div className="modal-footer bg-light border-top-0">
      <button
        type="button"
        className="btn btn-outline-secondary"
        onClick={handleModalClose}
      >
        {t("shared.cancel", "Cancel")}
      </button>
<button
  type="button"
  className="btn btn-primary"
  onClick={selectedEntry?.id ? handleUpdate : handleCreate}
  disabled={loading}
>
  {loading ? (
    <>
      <Spinner size="sm" className="me-2" />
      {selectedEntry?.id ? t("shared.updating", "Updating...") : t("shared.creating", "Creating...")}
    </>
  ) : (
    selectedEntry?.id ? t("shared.update_work_order", "Update Work Order") : t("shared.create_work_order", "Create Work Order")
  )}
</button>
    </div>
  </motion.div>
</Modal>
  {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
        <div className="modal-header">
          <h5 className="modal-title">{t("shared.confirm_delete", "Confirm Delete")}</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setDeleteModal(false)}
            aria-label={t("shared.close", "Close")}
          ></button>
        </div>
        <div className="modal-body">
          <p>
            {t("business.reactive.are_you_sure_you_want_to_delete_this_work_order", "Are you sure you want to delete this work order? This action cannot be undone.")}
          </p>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setDeleteModal(false)}
          >
            {t("shared.cancel", "Cancel")}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleDelete}
          >
            {t("shared.delete", "Delete")}
          </button>
        </div>
      </Modal>

      {/* Edit Scope Dialog disabled — occurrences are independent */}
      {/* <Modal isOpen={editScopeDialog.isOpen} toggle={() => setEditScopeDialog({ isOpen: false, pendingPayload: null })} centered>
        <div className="modal-header">
          <h5 className="modal-title">{t('business.reactive.edit_recurring_wo', 'Edit Recurring Work Order')}</h5>
          <button type="button" className="btn-close" onClick={() => setEditScopeDialog({ isOpen: false, pendingPayload: null })} />
        </div>
        <div className="modal-body">
          <p className="text-muted mb-4">
            {t('business.reactive.edit_scope_description', 'This is a recurring preventive work order. How would you like to apply your changes?')}
          </p>
          <div className="d-grid gap-2">
            <Button
              color="primary"
              onClick={() => handleEditScopeConfirm(true)}
              disabled={loading}
            >
              <FeatherIcon icon="layers" size={16} className="me-2" />
              {t('business.reactive.this_and_following', 'This and following work orders')}
            </Button>
            <Button
              color="outline-primary"
              onClick={() => handleEditScopeConfirm(false)}
              disabled={loading}
            >
              <FeatherIcon icon="edit-2" size={16} className="me-2" />
              {t('business.reactive.this_only', 'This work order only')}
            </Button>
          </div>
        </div>
      </Modal> */}

    </div>
  );
};

export default Transactions;